import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetTitle
} from '@invent/wl-ui-kit-next/ui';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRight,
  X
} from 'lucide-react';
import * as React from 'react';
import {
  Link,
  Navigate,
  NavigationType,
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
  UNSAFE_RouteContext,
  createPath,
  useLocation,
  useNavigate,
  useNavigationType,
  useRoutes,
  useSearchParams,
  type Location,
  type Navigator,
  type RouteObject,
  type To
} from 'react-router-dom';

import { isField } from './is-field';
import { NewTabLink } from './new-tab-link';
import { StatusChip } from './status-chip';
import { readViewerChoice, writeViewerChoice } from './viewer-store';
import { SheetZone, useViewer, useWorkspaceWidth } from './zone';

/**
 * **Open beside the list** (SR-530) — the Team Portal's one way to open something from a list and
 * work down the list with it open. Two kinds of what opens, one behaviour:
 *
 * - **a row's panel** (Lists, DD calls, Metrics updates) — the row's working context, beside the
 *   whole list page: its cards, filters and columns stay (`PaneSplit`, `useOpenParam`);
 * - **a record** (Compliance queues, My tasks, Number checks, People, Teams, Firms, Responses,
 *   Reports, Rankings, Import, and the setup areas) — a page of its own at its own address, the list
 *   shrunk to a 320 px column beside it (`SplitView`, `SplitList`; `ListArea` in the admin domain).
 *
 * What the reader handles is the same for both: what is open is in the address, so Back closes it
 * and a reload or a shared link opens it again; Esc closes it (`escapeIsFree`); ↑ and ↓ walk the list
 * and open what they stop on after `FOLLOW_MS`, never past something unsaved (`SPLIT_HOLD`); the
 * open thing's header has *Previous*, *Next*, ↗ in a new tab and ✕ (`OpenNav`); a row carries both
 * ways in at its start (`RowOpens`); *Opens in* (`PaneControl`) is the reader's one choice for every
 * page — a reading pane or a new tab, and the pane beside the list, below it or a sheet over it. For
 * a record, "beside" is the column, and "below" is the column too: a record under a list cannot be
 * read. Below 1440 px the app's rail folds to its icons whenever something is open beside a list.
 */

/** How long ↑ / ↓ wait on a row before opening it: fast steps move the highlight alone, and what is
 *  open follows where the reader stops. */
export const FOLLOW_MS = 300;

/** The app's narrowest width with room for a record beside the list's column (`lg`, 1024 px); an
 *  area whose record needs more says so (`columnFrom` — Reports, 1280). */
const COLUMN_FROM = 1024;

interface ISplitContext {
  /** The list's column is drawn beside the record. */
  shown: boolean;
  /** The window has room for the column at all. */
  roomy: boolean;
  /** The record is in a sheet over the list page. */
  sheet: boolean;
  toggle: () => void;
  /** The column's rows, as the list hands them in (`SplitList`), and the open one. */
  register: (items: readonly ISplitItem[], selected: Nullable<string>) => void;
  nav: IOpenNav;
  /** The rows a record wrote to while open in this session (`writes`), by the column's key — in a
   *  snapshot column, the rows found gone from their list after such a write (`markDone`). */
  written: ReadonlySet<string>;
  /** How many writes the open records have said changed the list (`SplitView`'s `writes`). */
  writes: number;
  /** A snapshot column's row found no longer on its list after a write (`IListOrigin`'s check). */
  markDone: (key: string) => void;
  /** The column is the rows as drawn when the record was opened, never read again
   *  (`IListOrigin`): a row written to is done; a live column's is done once it leaves the list. */
  snapshot: boolean;
}

const SplitContext = React.createContext<Nullable<ISplitContext>>(null);

/** Drawn inside a record, this keeps Esc, ↑ / ↓ and *Previous* / *Next* from leaving it — spread it
 *  on the element that must not be lost (`{...SPLIT_HOLD}`). */
export const SPLIT_HOLD = { 'data-split-hold': '' } as const;

/** Inside a record drawn in a split view: whether its list is shown, the switch for it, and the
 *  record's way on, back, out and to a new tab. */
export function useSplit(): Nullable<ISplitContext> {
  return React.useContext(SplitContext);
}

/**
 * A list beside a record (the two-portals design, §4.3 and §5) — the frame every list-and-record
 * area is drawn in.
 *
 * **With no record open, the list is the page**: its title is the `h1`, and it is the page's one
 * `main`. **With a record open, the record is the page's one `main`**, and the list — when it is
 * shown — sits beside it in a 320 px column that is a complementary landmark titled by an `h2`. That
 * is why the Team Portal's shell draws no `main` of its own: a spec reading "the page" reads the
 * record, and never finds the list's rows or links among the record's.
 *
 * **The column is shown when the record was opened from its list** — the list's links leave its
 * search in the history entry's state — **or when the record's address carries the list's
 * query**; otherwise the record is full width, and "Show list" puts the list back. Esc and ✕ close
 * the record, back to the list under the search it was opened from — but never out of a field, and
 * never while a menu or a dialog is open, whose own Esc it is.
 *
 * **A record can hold itself open**, by drawing an element marked `SPLIT_HOLD` for as long as
 * leaving it would destroy something: an inline field being edited or saved (`InlineField`), or a
 * work item's write under way. A marker in the DOM rather than a hold on this context, for
 * `overlayOpen`'s reason: the question is asked of the page at the moment of the key.
 *
 * **Below `columnFrom`** (1024 px, or the record's own need) there is no room for two panes: the
 * column is not drawn and there is nothing to show it with, so the record is alone. **In a sheet**
 * (`layout` "sheet" and the list page handed in as `index`, a work page's *Opens in*): the list page
 * stays the page, read under the search the record was opened from, and the record is a wide sheet
 * over it; Esc or ✕ closes it.
 */
export function SplitView({
  record,
  listInitially,
  paneTitle,
  pane,
  closeTo,
  columnFrom = COLUMN_FROM,
  writes = 0,
  snapshot = false,
  children
}: {
  /** Whether a record is open — the route below names one. */
  record: boolean;
  /** Whether the list starts shown beside it: opened from the list, or its query in the address. */
  listInitially: boolean;
  /** The pane's `h2`. */
  paneTitle: string;
  /** The list, drawn in the pane — rendered only while it is shown. */
  pane: React.ReactNode;
  /** Where Esc goes: the list, under the search the record was opened from. */
  closeTo: string;
  /** The narrowest window, in px, with room for the record beside the column. */
  columnFrom?: number;
  /** How many writes the open records have said changed what the list draws (`useListReread`):
   *  each marks the open row written. */
  writes?: number;
  /** The column is a snapshot of another area's list (`IListOrigin`). */
  snapshot?: boolean;
  /** The list page, or the record. */
  children: React.ReactNode;
}) {
  // A record arriving by its own address — a new tab, a reload, a shared link — is full width with
  // *Show list*, whatever *Opens in* says; one a press in its list opened has the column.
  const arrival = useArrival();
  const fromList = listInitially && !arrival;
  const [shown, setShown] = React.useState(fromList);
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = React.useId();
  const roomy = useMediaQuery(`(min-width: ${columnFrom}px)`);
  // Drawn inside a record's sheet over its list page (`RecordSheetHost`): the record alone.
  const sheet = useInSheet() && record;

  React.useEffect(() => {
    if (fromList) setShown(true);
  }, [fromList]);
  React.useEffect(() => {
    if (!record) setShown(false);
  }, [record]);

  useWorkspaceWidth('split', record && shown && roomy && !sheet);

  const close = React.useCallback(() => navigate(closeTo), [navigate, closeTo]);
  useEscapeClose(close, record && !sheet);

  // The column's rows, handed in by the list it draws, for the record's *Previous* and *Next*.
  const [listed, setListed] = React.useState<{ items: readonly ISplitItem[]; selected: Nullable<string> }>({
    items: [],
    selected: null
  });
  const register = React.useCallback(
    (items: readonly ISplitItem[], selected: Nullable<string>) =>
      setListed((now) => (now.items === items && now.selected === selected ? now : { items, selected })),
    []
  );
  const at = listed.items.findIndex((item) => item.key === listed.selected);

  // The rows written to in this session, kept per list (its address) on the browser's session, so
  // a record of another area opened from the same column keeps them; going back to the list
  // itself — which reads it again — forgets them.
  const writtenKey = `${WRITTEN_KEY}${closeTo}`;
  const [written, setWritten] = React.useState<ReadonlySet<string>>(() => readWritten(writtenKey));
  React.useEffect(() => setWritten(readWritten(writtenKey)), [writtenKey]);
  React.useEffect(() => {
    if (record) return;
    forgetWritten(closeTo);
    setWritten((now) => (now.size === 0 ? now : new Set()));
  }, [record, closeTo]);
  const selectedNow = React.useRef(listed.selected);
  selectedNow.current = listed.selected;
  const markWritten = React.useCallback(
    (key: string) =>
      setWritten((now) => {
        if (now.has(key)) return now;
        const next = new Set(now).add(key);
        writeWritten(writtenKey, next);
        return next;
      }),
    [writtenKey]
  );
  // A live column: the open row written to — done once the list read again drops it
  // (`SplitList`). A snapshot column asks its list itself (`markDone`).
  const firstWrites = React.useRef(writes);
  React.useEffect(() => {
    if (snapshot || writes === firstWrites.current) return;
    const key = selectedNow.current;
    if (key !== null) markWritten(key);
  }, [writes, snapshot, markWritten]);
  const go = (item: ISplitItem | undefined) =>
    item ? () => !held() && navigate(item.to, { state: item.state, replace: true }) : null;
  // The list's rows are known while it is beside the record, or under its sheet (the column drawn
  // hidden there, for them alone).
  const walks = shown || sheet;
  const nav: IOpenNav = {
    previous: walks && at > 0 ? go(listed.items[at - 1]) : null,
    next: walks && at >= 0 && at < listed.items.length - 1 ? go(listed.items[at + 1]) : null,
    newTab: `${location.pathname}${location.search}`,
    onClose: close
  };

  const context: ISplitContext = {
    shown: shown && roomy && !sheet,
    roomy: roomy && !sheet,
    sheet,
    toggle: () => setShown((current) => !current),
    register,
    nav,
    written,
    writes,
    markDone: markWritten,
    snapshot
  };

  if (!record) return <main className="min-w-0 flex-1">{children}</main>;

  if (sheet) {
    // In a sheet over its list page, *Previous* / *Next* walk the rows that page draws — their ↗
    // links — with no read of the list again.
    const rows = pageRows();
    const here = rows.findIndex((path) => leadsTo(path, location.pathname));
    const step = (to: string | undefined) =>
      to ? () => !held() && navigate(to, { state: location.state, replace: true }) : null;
    return (
      <SplitContext.Provider
        value={{
          ...context,
          nav: { ...nav, previous: here > 0 ? step(rows[here - 1]) : null, next: here >= 0 ? step(rows[here + 1]) : null }
        }}
      >
        {children}
      </SplitContext.Provider>
    );
  }

  // The column's list registers its rows here too, so the provider holds both panes.
  return (
    <SplitContext.Provider value={context}>
      <div className="flex min-w-0 flex-1">
        {context.shown && (
          <aside
            aria-labelledby={titleId}
            className="sticky top-12 flex h-[calc(100dvh-3rem)] w-80 shrink-0 flex-col border-r border-border bg-white"
          >
            <h2
              id={titleId}
              className="px-4 pt-3.5 pb-1 text-base leading-tight font-medium text-[var(--color-royal-blue)]"
            >
              {paneTitle}
            </h2>
            {pane}
          </aside>
        )}
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </SplitContext.Provider>
  );
}

/** Where the rows written to are kept, per list address, for this browser session. */
const WRITTEN_KEY = 'veritant.split.written.';

function readWritten(key: string): ReadonlySet<string> {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(key) ?? '[]') as unknown;
    return new Set(Array.isArray(parsed) ? parsed.filter((one): one is string => typeof one === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeWritten(key: string, keys: ReadonlySet<string>): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify([...keys]));
  } catch {
    // Kept for this page load only.
  }
}

/** Forgets the rows written to beside the list at `list` (its address, search included) — the list
 *  read again, whose rows are what they now are. A list page whose rows open another area's
 *  records calls it as it draws (`useListOrigin`). */
export function forgetWritten(list: string): void {
  try {
    window.sessionStorage.removeItem(`${WRITTEN_KEY}${list}`);
  } catch {
    // Nothing kept.
  }
}

/** The records the page under a sheet draws, in its order: each row's ↗ (`RowOpens`). */
function pageRows(): string[] {
  return [...document.querySelectorAll<HTMLAnchorElement>('[data-row-opens] a[data-new-tab]')]
    .filter((link) => !link.closest('[data-record-sheet]'))
    .map((link) => `${link.pathname}${link.search}`);
}

/** Whether a row's address leads to the record at `pathname` — itself, or a tab under it. */
function leadsTo(row: string, pathname: string): boolean {
  const path = row.split(/[?#]/)[0];
  return pathname === path || pathname.startsWith(`${path}/`) || path.startsWith(`${pathname}/`);
}

/** The record header's switch for its list — drawn by `PageHeader` inside a split record. */
export function SplitToggle() {
  const split = useSplit();
  if (!split || !split.roomy) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={split.toggle}
      className="-ml-2 h-7 gap-1.5 px-2 font-normal text-muted-foreground"
    >
      {split.shown ? (
        <PanelLeftClose className="size-4" aria-hidden />
      ) : (
        <PanelLeftOpen className="size-4" aria-hidden />
      )}
      {split.shown ? 'Hide list' : 'Show list'}
    </Button>
  );
}

/** A split record's *Previous* / *Next* / ↗ / ✕ — drawn by `PageHeader` inside a split record. */
export function SplitNav() {
  const split = useSplit();
  if (!split) return null;
  return <OpenNav {...split.nav} />;
}

export interface ISplitItem {
  key: string;
  /** The record's address, and the state its link leaves — the list's search. */
  to: string;
  state?: unknown;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  trailing?: React.ReactNode;
}

/**
 * The list as the pane draws it: one line of the record's name, one of what tells it apart, a
 * chip at the right — a link each, the open one `aria-current`. ↑ and ↓ move between them and open
 * the one they stop on (`FOLLOW_MS`), as the record's *Previous* and *Next* do — never past a record
 * that holds itself open (`SPLIT_HOLD`), where they move the focus alone.
 *
 * Its words are the list's own: `status` is what it says in place of rows (loading, empty, past
 * the end, a read that failed), and `footer` its paging.
 */
export function SplitList({
  items,
  selected,
  status,
  footer,
  label
}: {
  items: Nullable<ISplitItem[]>;
  selected: Nullable<string>;
  status?: React.ReactNode;
  footer?: React.ReactNode;
  /** The list's accessible name. */
  label: string;
}) {
  const list = React.useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const follow = useFollow();
  const split = useSplit();
  const register = split?.register;
  // Rows dealt with in this session (written to while open): in a snapshot column every one is
  // done; in a live column one is done once the list read again has dropped it — it stays where it
  // was, so ↑ / ↓ and *Previous* / *Next* walk on from it.
  const known = React.useRef(new Map<string, { item: ISplitItem; at: number }>());
  const readAt = React.useRef<{ rows: Nullable<string>; writes: number }>({ rows: null, writes: 0 });
  const shown = React.useMemo(() => {
    if (items === null) return null;
    // Other rows with no write since the last ones: another page or search, whose earlier rows are
    // no rows dropped from this one. Rows are told apart by their keys — a list hands in a new array
    // on every draw.
    const rowsNow = items.map((item) => item.key).join('\n');
    const writesNow = split?.writes ?? 0;
    if (rowsNow !== readAt.current.rows) {
      if (readAt.current.rows !== null && writesNow === readAt.current.writes) known.current = new Map();
      readAt.current = { rows: rowsNow, writes: writesNow };
    }
    items.forEach((item, at) => known.current.set(item.key, { item, at }));
    const written = split?.written ?? new Set<string>();
    const present = new Set(items.map((item) => item.key));
    const rows: { item: ISplitItem; done: boolean }[] = items.map((item) => ({
      item,
      done: written.has(item.key) && (split?.snapshot ?? false)
    }));
    const gone = [...written]
      .filter((key) => !present.has(key) && known.current.has(key))
      .map((key) => known.current.get(key)!)
      .sort((a, b) => a.at - b.at);
    for (const { item, at } of gone) rows.splice(Math.min(at, rows.length), 0, { item, done: true });
    return rows;
  }, [items, split?.written, split?.writes, split?.snapshot]);
  const drawn = React.useMemo(() => shown?.map((row) => row.item) ?? null, [shown]);
  React.useEffect(() => {
    register?.(drawn ?? [], selected);
  }, [register, drawn, selected]);
  // Hidden, the column names no open row: a write then marks none.
  React.useEffect(() => () => register?.([], null), [register]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    const links = [...(list.current?.querySelectorAll<HTMLAnchorElement>(':scope > li > a') ?? [])];
    const index = links.indexOf(document.activeElement as HTMLAnchorElement);
    const target = listKeyTarget(event.key, index, links.length - 1);
    if (target === null) return;
    const at = Math.max(0, Math.min(links.length - 1, target));
    const link = links[at];
    if (link) {
      event.preventDefault();
      link.focus();
      const item = drawn?.[at];
      if (item && item.key !== selected) follow(() => navigate(item.to, { state: item.state, replace: true }));
    }
  };

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {shown === null || shown.length === 0 ? (
          <div className="px-4 py-3 text-[0.8125rem] text-muted-foreground">
            {/* Not the kit's skeleton: the suite counts those across the page, and the record's
                own loading is what it is asking about. */}
            {status ?? (
              <span aria-hidden className="grid gap-2">
                {Array.from({ length: 8 }, (_, index) => (
                  <span key={index} className="h-9 animate-pulse rounded-md bg-muted" />
                ))}
              </span>
            )}
          </div>
        ) : (
          <ul ref={list} aria-label={label} onKeyDown={onKeyDown} className="grid">
            {shown.map(({ item, done }) => {
              const current = item.key === selected;
              return (
                <li key={item.key} data-done={done ? '' : undefined}>
                  <Link
                    to={item.to}
                    state={item.state}
                    aria-current={current ? 'true' : undefined}
                    aria-description={done ? 'Done' : undefined}
                    className={cn(
                      'flex items-start gap-3 border-b border-border px-4 py-2 text-[0.8125rem] outline-none',
                      'hover:bg-[var(--accent)] focus-visible:bg-[var(--accent)] focus-visible:shadow-[inset_3px_0_0_var(--color-teal)]',
                      current &&
                        'bg-[color-mix(in_srgb,var(--color-teal)_8%,white)] shadow-[inset_3px_0_0_var(--color-teal)]'
                    )}
                  >
                    <span className={cn('grid min-w-0 flex-1 gap-0.5', done && 'opacity-60')}>
                      <span className="truncate font-medium text-[var(--color-royal-blue)]">
                        {item.primary}
                      </span>
                      {item.secondary && (
                        <span className="line-clamp-2 text-xs text-muted-foreground">
                          {item.secondary}
                        </span>
                      )}
                    </span>
                    {/* The name has the room: the chip takes at most under half the line. A row dealt
                        with says so in its place. */}
                    {(done || item.trailing) && (
                      <span className="flex max-w-[45%] min-w-0 shrink-0 justify-end pt-0.5">
                        {done ? <StatusChip tone="neutral">Done</StatusChip> : item.trailing}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {footer && (
        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2 text-xs text-muted-foreground">
          {footer}
        </div>
      )}
    </>
  );
}

/**
 * The pane's paging: where it is, and the way on and back. Worded apart from the list page's
 * pager — "Page 2 of 7 · 312 found" — because both can be on one screen, the record's own lists
 * below, and a spec reads a pager's count by its words.
 */
export function SplitPager({
  page,
  pageSize,
  total,
  onPage
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <>
      <span className="tabular-nums">
        Page {page} of {lastPage} · {total} found
      </span>
      <span className="flex gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          aria-label="Previous page of the list"
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
          aria-label="Next page of the list"
        >
          Next
        </Button>
      </span>
    </>
  );
}

/** Where a key moves focus in the pane's list, before clamping — or null for a key it ignores. */
function listKeyTarget(key: string, index: number, last: number): Nullable<number> {
  switch (key) {
    case 'Home':
      return 0;
    case 'End':
      return last;
    case 'ArrowDown':
      return index + 1;
    case 'ArrowUp':
      return index - 1;
    default:
      return null;
  }
}

/**
 * Whether an Esc is free to close a record — the split view's rule, and a list's reading pane's
 * (SR-230): never a repeat, never out of a field, never while a menu, listbox, popover or dialog is
 * open (whose Esc it is), and never while the page holds something no re-read brings back
 * (`SPLIT_HOLD`). Asked from a captured listener, so an overlay closing on this same press is still
 * in the page.
 */
export function escapeIsFree(event: KeyboardEvent): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented || event.repeat) return false;
  return !isField(event.target) && !overlayOpen() && !held();
}

/**
 * Closes a record on a free Esc (`escapeIsFree`) — the split view's, a list's reading pane's and DD
 * calls' panel's one listener. Captured, so a menu or a dialog closing on this same press is still
 * in the page when the question is asked; the latest `close` is called, never the first render's.
 */
export function useEscapeClose(close: () => void, enabled = true): void {
  const closeRef = React.useRef(close);
  closeRef.current = close;
  React.useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (!escapeIsFree(event)) return;
      event.preventDefault();
      closeRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled]);
}

/** A record holding itself open, over something closing it would destroy (`SPLIT_HOLD`). */
function held(): boolean {
  return document.querySelector('[data-split-hold]') !== null;
}

/** A menu, a listbox, a popover or a dialog open anywhere — whose Esc it is. */
function overlayOpen(): boolean {
  return (
    document.querySelector(
      '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]'
    ) !== null
  );
}

/**
 * ↑ / ↓ opening what they stop on: each call replaces the last one waiting, and runs `open` after
 * `FOLLOW_MS` — fast steps move the highlight alone. Nothing opens while what is open holds itself
 * (`SPLIT_HOLD`): the keys move the focus and ask nothing.
 */
export function useFollow(): (open: () => void) => void {
  const timer = React.useRef<number>();
  // A move before it fires — Esc, ✕, a click elsewhere — takes the step back: nothing reopens.
  const { key } = useLocation();
  React.useEffect(() => () => window.clearTimeout(timer.current), [key]);
  return React.useCallback((open: () => void) => {
    window.clearTimeout(timer.current);
    if (held()) return;
    timer.current = window.setTimeout(() => {
      if (!held()) open();
    }, FOLLOW_MS);
  }, []);
}

/** What is open, and the ways on, back, to a new tab and out — one shape for a panel and a record. */
export interface IOpenNav {
  /** The row before it in the list, or null at the first (or where the list is not beside it). */
  previous: Nullable<() => void>;
  next: Nullable<() => void>;
  /** Its own page, opened full width in a new tab. */
  newTab: Nullable<string>;
  onClose: () => void;
}

/**
 * The open thing's header controls, the same wherever something opens beside a list: *Previous* and
 * *Next* (the list's rows, as ↑ / ↓ walk them), ↗ its page in a new tab, ✕ close (as Esc does).
 * *Previous* and *Next* do nothing while what is open holds itself (`SPLIT_HOLD`).
 */
export function OpenNav({ previous, next, newTab, onClose }: IOpenNav) {
  return (
    <div className="flex shrink-0 items-center" data-open-nav="">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label="Previous"
        title="Previous"
        disabled={!previous}
        onClick={() => !held() && previous?.()}
      >
        <ChevronUp className="size-4" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label="Next"
        title="Next"
        disabled={!next}
        onClick={() => !held() && next?.()}
      >
        <ChevronDown className="size-4" aria-hidden />
      </Button>
      {newTab && (
        <Button asChild variant="ghost" size="icon" className="size-8">
          <Link to={newTab} target="_blank" rel="noopener" aria-label="Open in a new tab" title="Open in a new tab">
            <ExternalLink className="size-4" aria-hidden />
          </Link>
        </Button>
      )}
      <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Close" title="Close (Esc)" onClick={onClose}>
        <X className="size-4" aria-hidden />
      </Button>
    </div>
  );
}

/**
 * A row's panel in the address (`?open=<key>`, a key the page builds from ids, never from a data
 * value): opening pushes a history entry — Back closes it — and moving to a neighbour (↑ / ↓,
 * *Previous* / *Next*) replaces it; close pushes the list without it. A reload or a shared link opens
 * the same row once the list holds it. The row last drawn under the key is kept, so a row that left
 * the list after a write still shows its panel; a key the list never held leaves the address.
 */
export function useOpenParam<Row>(
  rows: Nullable<readonly Row[]>,
  keyOf: (row: Row) => string,
  /** Whether a row answers the address's key — `keyOf`'s equality, or also an older link's key
   *  (DD calls: a bare participant id from My tasks). */
  matches: (row: Row, key: string) => boolean = (row, key) => keyOf(row) === key
): {
  key: Nullable<string>;
  row: Nullable<Row>;
  open: (row: Row, options?: { replace?: boolean }) => void;
  close: (options?: { replace?: boolean }) => void;
} {
  const [params, setParams] = useSearchParams();
  const key = params.get(OPEN_PARAM);
  const found = key === null || rows === null ? undefined : rows.find((row) => matches(row, key));
  const last = React.useRef<Nullable<{ key: string; row: Row }>>(null);
  if (key !== null && found !== undefined) last.current = { key, row: found };
  const row = key === null ? null : (found ?? (last.current?.key === key ? last.current.row : null));
  const gone = key !== null && rows !== null && row === null;
  React.useEffect(() => {
    if (!gone) return;
    setParams(
      (now) => {
        const next = new URLSearchParams(now);
        next.delete(OPEN_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [gone, setParams]);
  const open = React.useCallback(
    (target: Row, options?: { replace?: boolean }) =>
      setParams(
        (now) => {
          const next = new URLSearchParams(now);
          next.set(OPEN_PARAM, keyOf(target));
          return next;
        },
        { replace: options?.replace ?? false }
      ),
    [setParams, keyOf]
  );
  const close = React.useCallback(
    (options?: { replace?: boolean }) =>
      setParams(
        (now) => {
          const next = new URLSearchParams(now);
          next.delete(OPEN_PARAM);
          return next;
        },
        { replace: options?.replace ?? false }
      ),
    [setParams]
  );
  return { key, row, open, close };
}

/** The address key of a row's open panel. */
export const OPEN_PARAM = 'open';

/** Where a row's press leads: the page's reading pane, or its page in a new tab. */
export type ViewOpens = 'pane' | 'tab';

const VIEW_OPENS_WORDS: Record<ViewOpens, string> = { pane: 'Reading pane', tab: 'New tab' };

/** How the reading pane is laid out: beside the list, below it, or a sheet over it. */
export type PaneLayout = 'beside' | 'below' | 'sheet';

const LAYOUT_WORDS: Record<PaneLayout, string> = {
  beside: 'Beside the list',
  below: 'Below the list',
  sheet: 'Sheet over the list'
};
const LAYOUTS = Object.keys(LAYOUT_WORDS) as PaneLayout[];

/** From 90rem (1440 px) the list has room for a row's panel beside it. */
const WIDE = '(min-width: 90rem)';

/** The choice's key on the browser, per viewer — one for every work page, Lists' own name kept so a
 *  choice made there holds. */
const KEY = 'veritant.lists.viewOpens';

/** Whether a media query matches, kept in step with the window. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export interface IPanePrefs {
  opens: ViewOpens;
  setOpens: (next: ViewOpens) => void;
  /** A row's panel's layout in force: the reader's choice, else beside from 1440 px and a sheet below
   *  it — never a sheet for a panel the page arrived with. */
  layout: PaneLayout;
  /** The layout *Opens in* says (its radio). */
  chosenLayout: PaneLayout;
  /** A record's: the column (the reader's beside or below, and the default) or a sheet. */
  recordLayout: 'beside' | 'sheet';
  setLayout: (next: PaneLayout) => void;
}

/**
 * The reader's *Opens in* — where a row leads and how what opens is laid out — kept on this browser
 * for the viewer as the work pages' page size is (`viewer-store.ts`), one for every page.
 */
export function usePanePrefs(): IPanePrefs {
  const key = `${KEY}.${useViewer()}`;
  const wide = useMediaQuery(WIDE);
  const [chosen, setChosen] = React.useState<Nullable<{ key: string; opens: ViewOpens; layout: Nullable<PaneLayout> }>>(null);
  const stored = readViewerChoice(key);
  const held = chosen?.key === key ? chosen : null;
  const opens: ViewOpens = held ? held.opens : stored.opens === 'tab' ? 'tab' : 'pane';
  const picked: Nullable<PaneLayout> = held
    ? held.layout
    : LAYOUTS.includes(stored.layout as PaneLayout)
      ? (stored.layout as PaneLayout)
      : null;
  const write = (next: { opens: ViewOpens; layout: Nullable<PaneLayout> }) => {
    setChosen({ key, ...next });
    writeViewerChoice(key, next.layout ? next : { opens: next.opens });
  };
  // A row's panel the address opened as the page arrived (a shared `?open=` link, a reload) is
  // never a sheet: the list is what the reader came to. Beside from 1440 px, else below it.
  const arrival = useArrival();
  const chosenLayout: PaneLayout = picked ?? (wide ? 'beside' : 'sheet');
  return {
    opens,
    setOpens: (next) => write({ opens: next, layout: picked }),
    layout: arrival && chosenLayout === 'sheet' ? (wide ? 'beside' : 'below') : chosenLayout,
    chosenLayout,
    recordLayout: picked === 'sheet' ? 'sheet' : 'beside',
    setLayout: (next) => write({ opens, layout: next })
  };
}

/**
 * *Opens in* ▾: where a row's press leads — the reading pane or a new tab — and how what opens is
 * laid out, said on the button by its mode. There is no pane toggle: every row opens by its own
 * icon, and ✕ or Esc closes it. `what` names what opens ("View on a row" on Lists); `kind` the
 * layouts offered — a row's panel beside, below or as a sheet; a record beside (the list's column)
 * or as a sheet.
 */
export function PaneControl({
  prefs,
  what = 'A row',
  kind = 'panel'
}: {
  prefs: IPanePrefs;
  what?: string;
  kind?: 'panel' | 'record';
}) {
  const { opens } = prefs;
  const words = VIEW_OPENS_WORDS;
  const Icon = opens === 'tab' ? ExternalLink : PanelRight;
  const layouts: PaneLayout[] = kind === 'record' ? ['beside', 'sheet'] : LAYOUTS;
  const value = kind === 'record' ? prefs.recordLayout : prefs.chosenLayout;
  return (
    <span className="inline-flex shrink-0 items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-8 gap-1.5 px-2.5 font-normal"
            aria-label={`Opens in: ${words[opens]}`}
            title={`Opens in: ${words[opens]}`}
            data-opens-in=""
          >
            <Icon className="size-4" aria-hidden />
            Opens in
            <ChevronDown className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">{what} opens</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={opens} onValueChange={(next) => prefs.setOpens(next as ViewOpens)}>
            <DropdownMenuRadioItem value="pane">{words.pane}</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="tab">{words.tab}</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">Reading pane</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={value} onValueChange={(next) => prefs.setLayout(next as PaneLayout)}>
            {layouts.map((layout) => (
              <DropdownMenuRadioItem key={layout} value={layout}>
                {LAYOUT_WORDS[layout]}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

/**
 * The ways into a row, at its start, whatever *Opens in* says: beside the list (its icon, pressed
 * while the row is the one open) and ↗ its page in a new tab. A page whose row opens a page of its
 * own, with nothing beside the list, draws ↗ alone (no `onPane`) — the same place on every work page.
 */
export function RowOpens({
  name,
  open,
  onPane,
  to,
  tabWords
}: {
  name: string;
  /** The row is the one open beside the list. */
  open?: boolean;
  /** The icon's press — none where the row opens nothing beside the list. */
  onPane?: () => void;
  /** The page ↗ opens. */
  to: string;
  /** ↗'s name — "Open Ann Hall — FA in a new tab". */
  tabWords: string;
}) {
  return (
    <span className="inline-flex items-center gap-1" data-row-opens="">
      {onPane && (
        <button
          type="button"
          aria-label={`Open ${name} beside the list`}
          title="Open beside the list"
          aria-pressed={open}
          data-row-pane=""
          onClick={onPane}
          className={cn(
            'inline-flex items-center rounded px-0.5 text-[var(--color-sapphire)] outline-none hover:text-[var(--color-royal-blue)] focus-visible:ring-2 focus-visible:ring-ring',
            open && 'text-[var(--color-royal-blue)]'
          )}
        >
          <PanelRight className="size-3.5" aria-hidden />
        </button>
      )}
      <NewTabLink to={to} label={tabWords} />
    </span>
  );
}

/** A row's panel's frame by where it is drawn: beside the list it sticks under the bars and scrolls
 *  on its own; below the list it is a card; in the sheet the sheet is its frame. */
export const PANE_FRAME: Record<PaneLayout, string> = {
  beside: 'rounded-md border border-border p-4 sticky top-16 max-h-[calc(100dvh-5rem)] overflow-y-auto',
  below: 'rounded-md border border-border p-4',
  sheet: 'p-1'
};

/** A row's press under *Opens in*: beside the list, or its page in a new tab. */
export function openRow(prefs: IPanePrefs, to: string, onPane: () => void) {
  if (prefs.opens === 'tab') window.open(to, '_blank', 'noopener');
  else onPane();
}

/**
 * A list and a row's panel, laid out by *Opens in*: beside the list (the panel's column only while
 * one is drawn), below it, or a sheet from the right over it — the default below 1440 px; Esc or ✕
 * closes it. `width` is the panel's column beside the list; `title` names the sheet for a screen
 * reader. Beside the list, the app's rail folds to its icons below 1440 px, as for a record.
 */
export function PaneSplit({
  prefs,
  pane,
  title,
  width,
  onClose,
  children
}: {
  prefs: IPanePrefs;
  /** The panel, or nothing while no row is open. */
  pane: React.ReactNode;
  title: string;
  width: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const drawn = pane !== null && pane !== undefined && pane !== false;
  const sheet = prefs.layout === 'sheet';
  useWorkspaceWidth('split', drawn && prefs.layout === 'beside');
  useEscapeClose(onClose, drawn && !sheet);
  return (
    <>
      <div
        className="grid items-start gap-3"
        style={drawn && prefs.layout === 'beside' ? { gridTemplateColumns: `minmax(0,1fr) ${width}` } : undefined}
      >
        {children}
        {drawn && !sheet && pane}
      </div>
      <Sheet open={drawn && sheet} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          onEscapeKeyDown={keepSheetOnEscape}
          onPointerDownOutside={keepSheetOnPress}
          className="w-[min(44rem,calc(100vw-2rem))] gap-0 overflow-y-auto p-3"
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          {drawn && sheet && pane}
        </SheetContent>
      </Sheet>
    </>
  );
}

/** A sheet's Esc is a field's while one is being edited, and nobody's while what is open holds
 *  itself (`SPLIT_HOLD`) — an overlay inside the sheet takes its own Esc first. */
function keepSheetOnEscape(event: KeyboardEvent): void {
  if (isField(event.target) || held()) event.preventDefault();
}

/** A press outside a sheet closes it, unless what is open holds itself (`SPLIT_HOLD`). */
function keepSheetOnPress(event: Event): void {
  if (held()) event.preventDefault();
}

/** The address key of a record open in a sheet over the page (`RecordSheetHost`). */
export const SHEET_PARAM = 'sheet';

/** The locations of the direct address this page load began at — a new tab, a reload, a shared
 *  link — and those that replaced it (a step to *Next*, a hash revealed), not a press in a list. */
const arrivalKeys = new Set<string>();
let lastKey: Nullable<string> = null;

/** Whether `location` is the direct address the page load began at, or one that replaced it, as
 *  opposed to one an in-app press led to. A record arriving so is full width, whatever *Opens in*
 *  says; a row's panel arriving so is never a sheet. */
export function useArrival(): boolean {
  const { key } = useLocation();
  const type = useNavigationType();
  if (arrivalKeys.size === 0) arrivalKeys.add(key);
  else if (key !== lastKey && type === NavigationType.Replace && lastKey !== null && arrivalKeys.has(lastKey)) {
    arrivalKeys.add(key);
  }
  lastKey = key;
  return arrivalKeys.has(key);
}

interface ISheetHost {
  /** Opens the record at `to` in a sheet over this page — a history entry, so Back closes it. */
  open: (to: string, state?: unknown) => void;
  /** Says a write inside the sheet changed what the page's list draws. */
  onWrite: () => void;
}

const SheetHostContext = React.createContext<Nullable<ISheetHost>>(null);

/** The sheet a record is drawn in over its list page, where there is one (`RecordSheetHost`). */
const InSheetContext = React.createContext(false);

/** Whether this is drawn inside a record's sheet — a split view then draws the record alone. */
export function useInSheet(): boolean {
  return React.useContext(InSheetContext);
}

/** The host a list writes to after a write in its record's sheet (`ListArea`'s reread). */
export function useSheetHost(): Nullable<ISheetHost> {
  return React.useContext(SheetHostContext);
}

/**
 * Opens a record from a list beside it, as *Opens in* says: a sheet over this very page — the list
 * kept as it is, its scroll, ticks and filters, no read again — or the record at its own address
 * with the list as its column (`SplitView`).
 */
export function useOpenRecord(): (to: string, state?: unknown) => void {
  const host = useSheetHost();
  const prefs = usePanePrefs();
  const navigate = useNavigate();
  return (to, state) => (prefs.recordLayout === 'sheet' && host ? host.open(to, state) : navigate(to, { state }));
}

/** Where a page's scrolling happens and what is stuck at its top: the window under the app bar, or
 *  a sheet's own body with nothing over it (a record in `RecordSheetHost`). */
export interface IScrollRoot {
  element: Nullable<HTMLElement>;
  /** The px stuck over the content at the scroller's top (the app bar's 48 on a page). */
  top: number;
}

const ScrollRootContext = React.createContext<IScrollRoot>({ element: null, top: 48 });

export function useScrollRoot(): IScrollRoot {
  return React.useContext(ScrollRootContext);
}

/**
 * A record in a sheet over the page it was opened from (SR-530, *Opens in* "Sheet over the list"):
 * the page stays mounted under it as it was — no read again, its scroll, ticks and filters kept —
 * and the record's address is the page's `?sheet=`, so Back closes it. The record is the app's own
 * route drawn at that address (`routes`, the zone's), its presses kept inside the sheet; a press to
 * the page's own address (the record's way back, ✕, Esc) closes it. Nothing it draws reaches the
 * app bar (`SheetZone`). Writes it says changed the list (`onWrite`) refresh the page quietly once
 * the sheet closes — the page's own *Refresh*, which keeps its rows while it reads. A direct address
 * holding a sheet (a reload, a shared link) opens the record at its own address, full width.
 */
export function RecordSheetHost({
  routes,
  refresh,
  children
}: {
  routes: RouteObject[];
  /** The page's *Refresh* (the app bar's), run after the sheet closes on a write. */
  refresh: () => Nullable<IOpenRefresh>;
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const arrival = useArrival();
  const params = new URLSearchParams(location.search);
  const at = ownPath(params.get(SHEET_PARAM));
  const sheetState = (location.state as Nullable<{ sheetState?: unknown }>)?.sheetState;
  const pageSearch = (() => {
    const next = new URLSearchParams(location.search);
    next.delete(SHEET_PARAM);
    const rendered = next.toString();
    return rendered ? `?${rendered}` : '';
  })();
  const outer = { pathname: location.pathname, search: pageSearch };
  const written = React.useRef(0);
  const title = React.useRef<Nullable<string>>(null);
  const [body, setBody] = React.useState<Nullable<HTMLElement>>(null);
  // A page with no *Refresh* of its own is drawn again instead, after a write in its sheet.
  const [drawnAgain, setDrawnAgain] = React.useState(0);

  const host = React.useMemo<ISheetHost>(
    () => ({
      open: (to, state) => {
        const next = new URLSearchParams(location.search);
        next.set(SHEET_PARAM, to);
        title.current = document.title;
        written.current = 0;
        const own = location.state && typeof location.state === 'object' ? location.state : {};
        navigate({ pathname: location.pathname, search: `?${next.toString()}` }, { state: { ...own, sheetState: state } });
      },
      onWrite: () => {
        written.current += 1;
      }
    }),
    [location, navigate]
  );

  const open = at !== null && !arrival;
  const wasOpen = React.useRef(open);
  React.useEffect(() => {
    if (wasOpen.current && !open) {
      if (title.current !== null) document.title = title.current;
      if (written.current > 0) {
        const pageRefresh = refresh();
        if (pageRefresh) pageRefresh.onClick();
        else setDrawnAgain((count) => count + 1);
      }
      written.current = 0;
    }
    wasOpen.current = open;
  }, [open, refresh]);

  if (at !== null && arrival) return <Navigate to={at} replace />;

  // The page's own history state goes back with it, the sheet's left out.
  const close = () => {
    const { sheetState: _sheet, ...own } = (location.state && typeof location.state === 'object' ? location.state : {}) as {
      sheetState?: unknown;
    };
    navigate(outer, { state: Object.keys(own).length > 0 ? own : null });
  };
  return (
    <SheetHostContext.Provider value={host}>
      {/* The page under the sheet sees its own address — as it was before the sheet opened, no
          `?sheet=` — so nothing it reads by the address is read again. */}
      <PageLocation sheet={at !== null}>
        <React.Fragment key={drawnAgain}>{children}</React.Fragment>
      </PageLocation>
      <Sheet open={open} onOpenChange={(next) => !next && close()}>
        <SheetContent
          ref={setBody}
          side="right"
          data-record-sheet=""
          // Nothing is stuck over the sheet's top: a record's sticky bars (its *Jump to*) stick there.
          style={{ '--sticky-top': '0px' } as React.CSSProperties}
          className="w-[min(64rem,calc(100vw-4rem))] max-w-none gap-0 overflow-y-auto p-0 sm:max-w-none"
        >
          <SheetTitle className="sr-only">Record</SheetTitle>
          {open && at !== null && (
            <ScrollRootContext.Provider value={{ element: body, top: 0 }}>
              <SheetRoutes
                routes={routes}
                at={at}
                state={sheetState}
                outer={outer}
                onClose={close}
                onMove={(to, state, replace) => {
                  const next = new URLSearchParams(location.search);
                  next.set(SHEET_PARAM, to);
                  const own = location.state && typeof location.state === 'object' ? location.state : {};
                  navigate(
                    { pathname: location.pathname, search: `?${next.toString()}` },
                    { state: { ...own, sheetState: state }, replace }
                  );
                }}
              />
            </ScrollRootContext.Provider>
          )}
        </SheetContent>
      </Sheet>
    </SheetHostContext.Provider>
  );
}

/** A sheet's address as the app's own path — never another origin's, which a crafted link could name. */
function ownPath(at: Nullable<string>): Nullable<string> {
  if (at === null || !at.startsWith('/') || at.startsWith('//') || at.includes('\\')) return null;
  try {
    return new URL(at, window.location.origin).origin === window.location.origin ? at : null;
  } catch {
    return null;
  }
}

/** The page's own location while a sheet is open over it: the one it had before the sheet opened. */
function PageLocation({ sheet, children }: { sheet: boolean; children: React.ReactNode }) {
  const outer = React.useContext(UNSAFE_LocationContext);
  const before = React.useRef(outer);
  if (!sheet) before.current = outer;
  return <UNSAFE_LocationContext.Provider value={before.current}>{children}</UNSAFE_LocationContext.Provider>;
}

/** A page's *Refresh*, as the app bar holds it. */
interface IOpenRefresh {
  onClick: () => void;
}

/** The app's routes drawn at the sheet's own address: a location of its own, presses that move the
 *  sheet (a history entry each, as on a page) or close it where they lead to the page under it. */
function SheetRoutes({
  routes,
  at,
  state,
  outer,
  onClose,
  onMove
}: {
  routes: RouteObject[];
  at: string;
  state: unknown;
  outer: { pathname: string; search: string };
  onClose: () => void;
  onMove: (to: string, state: unknown, replace: boolean) => void;
}) {
  const navigation = React.useContext(UNSAFE_NavigationContext);
  const location = React.useMemo<Location>(() => {
    const url = new URL(at, window.location.origin);
    return { pathname: url.pathname, search: url.search, hash: url.hash, state: state ?? null, key: `sheet:${at}` };
  }, [at, state]);
  const move = React.useRef({ onClose, onMove, outer });
  move.current = { onClose, onMove, outer };
  const value = React.useMemo(() => {
    const go = (to: To, next: unknown, replace: boolean) => {
      const path = typeof to === 'string' ? to : createPath(to);
      const url = new URL(path, window.location.origin);
      // The page under the sheet — the record's way back, ✕, Esc: the sheet closes.
      if (url.pathname === move.current.outer.pathname) return move.current.onClose();
      move.current.onMove(`${url.pathname}${url.search}${url.hash}`, next, replace);
    };
    const navigator: Navigator = {
      ...navigation.navigator,
      push: (to, next) => go(to, next, false),
      replace: (to, next) => go(to, next, true),
      go: (delta) => navigation.navigator.go(delta)
    };
    return { ...navigation, navigator };
  }, [navigation]);
  return (
    <UNSAFE_NavigationContext.Provider value={value}>
      <UNSAFE_LocationContext.Provider value={{ location, navigationType: NavigationType.Push }}>
        <UNSAFE_RouteContext.Provider value={{ outlet: null, matches: [], isDataRoute: false }}>
          <InSheetContext.Provider value>
            <SheetZone>
              <SheetRouted routes={routes} location={location} />
            </SheetZone>
          </InSheetContext.Provider>
        </UNSAFE_RouteContext.Provider>
      </UNSAFE_LocationContext.Provider>
    </UNSAFE_NavigationContext.Provider>
  );
}

function SheetRouted({ routes, location }: { routes: RouteObject[]; location: Location }) {
  return useRoutes(routes, location);
}
